import { useState } from 'react';

import { Center } from '../components/steps/StepBookingCenter';
import { Country } from '../components/steps/StepBookingCountry';
import { BookNowOption } from '../components/steps/StepBookNowChoice';
import { Certification } from '../components/steps/StepCertification';
import { Language } from '../components/steps/StepCertificationLanguage';
import { Method } from '../components/steps/StepCertificationMethod';
import { Location } from '../components/steps/StepLocation';
import { TrainingCompletedOption } from '../components/steps/StepTrainingCompleteChoice';
import { AnalyticsAction, sendAnalyticsData } from '../vendors/GoogleAnalytics';
import { useStateWithEffect } from './useStateWithEffect';

export default function useStepperState(): {
  activeStep: number;
  bookNow: BookNowOption | null;
  setBookNow: (value: BookNowOption | null) => void;
  trainingComplete: TrainingCompletedOption | null;
  setTrainingComplete: (value: TrainingCompletedOption | null) => void;
  center: Center | null;
  setCenter: (value: Center | null) => void;
  certification: Certification | null;
  setCertification: (value: Certification | null) => void;
  country: Country | null;
  setCountry: (value: Country | null) => void;
  language: Language | null;
  setLanguage: (value: Language | null) => void;
  trainingLanguage: Language | null;
  setTrainingLanguage: (value: Language | null) => void;
  location: Location | null;
  setLocation: (value: Location | null) => void;
  method: Method | null;
  setMethod: (value: Method | null) => void;
} {
  const [activeStep, setActiveStep] = useState(0);

  const [certification, setCertification] = useStateWithEffect<Certification>((certification) => {
    // Reset downstream
    setCenter(null);
    setCountry(null);
    setLanguage(null);
    setLocation(null);
    setMethod(null);
    setTrainingComplete(null);
    setTrainingLanguage(null);

    if (!certification) {
      setActiveStep(0);
      return;
    }
    sendAnalyticsData(AnalyticsAction.SELECT_CERTIFICATION, certification.label);
    setActiveStep(1);
  }, []);

  const [language, setLanguage] = useStateWithEffect<Language>(
    (language) => {
      // Reset downstream
      setCenter(null);
      setCountry(null);
      setLocation(null);
      setMethod(null);
      setTrainingComplete(null);
      setTrainingLanguage(null);

      if (!language) {
        setActiveStep(1);
        return;
      }
      sendAnalyticsData(AnalyticsAction.SELECT_LANGUAGE, language.label);
      setActiveStep(activeStep > 2 ? activeStep : 2);
    },
    [activeStep]
  );

  const [method, setMethod] = useStateWithEffect<Method>((method) => {
    // Reset downstream
    setCenter(null);
    setCountry(null);
    setLocation(null);
    setTrainingComplete(null);
    setTrainingLanguage(null);

    if (!method) {
      setActiveStep(2);
      return;
    }
    sendAnalyticsData(AnalyticsAction.SELECT_METHOD, method);
    if (method === Method.SELFSTUDY_EXAM) {
      // Skip step "Your downloads for self-study"
      setActiveStep(4);
    } else {
      setActiveStep(3);
    }
  }, []);

  const [bookNow, setBookNow] = useStateWithEffect<BookNowOption>((bookNow) => {
    // User has selected "Self study and online exam" as method
    sendAnalyticsData(AnalyticsAction.SELECT_BOOKING_METHOD, bookNow);
    if (bookNow === BookNowOption.YES) {
      setActiveStep(5);
    }
  }, []);

  const [trainingComplete, setTrainingComplete] = useStateWithEffect<TrainingCompletedOption>(
    (trainingComplete) => {
      // User has selected "Self study and online exam" as method
      sendAnalyticsData(AnalyticsAction.SELECT_BOOKING_METHOD, trainingComplete);
      if (trainingComplete === TrainingCompletedOption.YES) {
        setActiveStep(4);
      }
    },
    []
  );

  const [trainingLanguage, setTrainingLanguage] = useStateWithEffect<Language>(
    (language) => {
      if (!language) {
        setActiveStep(3);
        return;
      }
      sendAnalyticsData(AnalyticsAction.SELECT_TRAINING_LANGUAGE, language.label);
      setActiveStep(4);
    },
    [activeStep]
  );

  const [location, setLocation] = useStateWithEffect<Location>((location) => {
    sendAnalyticsData(AnalyticsAction.SELECT_LOCATION, `${location?.label} ${location?.range}`);
  }, []);

  const [country, setCountry] = useStateWithEffect<Country>(
    (country) => {
      // Reset downstream
      setCenter(null);
      setLocation(null);

      sendAnalyticsData(AnalyticsAction.SELECT_EXAM_COUNTRY, country?.label);
      if (method === Method.EXAM_ONLY && !certification?.trainingNeeded && activeStep <= 4) {
        setActiveStep(4);
      } else if (method === Method.EXAM_ONLY && certification?.trainingNeeded && activeStep <= 5) {
        setActiveStep(5);
      } else if (method === Method.SELFSTUDY_EXAM && activeStep <= 6) {
        setActiveStep(6);
      } else {
        setActiveStep(activeStep - 1);
      }
    },
    [method, activeStep]
  );

  const [center, setCenter] = useStateWithEffect<Center>(
    (center) => {
      sendAnalyticsData(AnalyticsAction.SELECT_EXAM_CENTER, center?.label);
      if (method === Method.EXAM_ONLY && !certification?.trainingNeeded && activeStep < 5) {
        setActiveStep(5);
      } else if (method === Method.EXAM_ONLY && certification?.trainingNeeded && activeStep < 6) {
        setActiveStep(6);
      } else if (method === Method.SELFSTUDY_EXAM && activeStep < 7) {
        setActiveStep(7);
      } else {
        setActiveStep(activeStep);
      }
    },
    [method, activeStep]
  );

  return {
    activeStep,
    bookNow,
    setBookNow,
    center,
    setCenter,
    certification,
    setCertification,
    country,
    setCountry,
    language,
    setLanguage,
    trainingComplete,
    setTrainingComplete,
    trainingLanguage,
    setTrainingLanguage,
    location,
    setLocation,
    method,
    setMethod,
  };
}
